import { z } from 'zod'

/**
 * 범용 감사로그 계약 (BIZ-93). 설계 명세서 부록 B #5.
 *
 * `audit_log` 테이블은 이미 있으나 미사용이었다. 이 계약은 **운영자 책임추적**
 * (누가·언제·어디서 민감/관리 액션을 했나)만 기록하는 축으로 좁힌다. 주문 상태
 * 타임라인 같은 도메인 이력은 order_status_history 등 기존 테이블에 위임하고
 * 중복하지 않는다.
 *
 * entity/action은 자유 문자열 대신 아래 카탈로그로 고정한다 — 배선 지점이 여러 곳에
 * 흩어져 있어 오타가 조용히 새는 걸 막는다. 새 이벤트는 여기 먼저 추가한 뒤 배선한다.
 */

/** 감사 대상 엔티티. audit_log.entity에 그대로 들어간다. */
export const auditEntity = z.enum(['company', 'order', 'payout', 'payment'])
export type AuditEntity = z.infer<typeof auditEntity>

/** 감사 액션. audit_log.action에 그대로 들어간다. */
export const auditAction = z.enum([
  'approval_decided', // company: 가승인 승인/반려
  'contract_status_changed', // company: 계약상태 레버 변경
  'referral_toggled', // company: 추천 효력 수동 만료/복구 (BIZ-132)
  'biz_verified', // company: 사업자 검증 상태 수동 처리 (BIZ-85)
  'transition', // order: 운영자 상태전이(from→to)
  'po_updated', // order: 발주 스냅샷(공급 단가·배송비) 수정
  'created', // payout: 묶음 지급 생성
  'paid', // payout: 지급 실행
  'cancelled', // payout: 지급 취소 / payment: 운영자 환불
  'postpaid_requested', // company: 후불 전환 셀프 신청 (BIZ-116)
  'postpaid_decided', // company: 운영자 후불 신청 승인/반려
  'postpaid_suspended', // company: 미납 자동정지 (후불→선불 복귀)
  'postpaid_resumed', // company: 완납 후 후불 자동 재개
])
export type AuditAction = z.infer<typeof auditAction>

/**
 * 배선에서 쓰는 이벤트 카탈로그. writeAudit에 이 값 중 하나를 넘긴다.
 * (entity, action) 조합을 한곳에서 못박아, 호출부가 문자열을 직접 조립하지 않게 한다.
 */
export const AUDIT_EVENTS = {
  COMPANY_APPROVAL_DECIDED: { entity: 'company', action: 'approval_decided' },
  COMPANY_CONTRACT_STATUS_CHANGED: { entity: 'company', action: 'contract_status_changed' },
  COMPANY_REFERRAL_TOGGLED: { entity: 'company', action: 'referral_toggled' },
  COMPANY_BIZ_VERIFIED: { entity: 'company', action: 'biz_verified' },
  ORDER_TRANSITION: { entity: 'order', action: 'transition' },
  ORDER_PO_UPDATED: { entity: 'order', action: 'po_updated' },
  PAYOUT_CREATED: { entity: 'payout', action: 'created' },
  PAYOUT_PAID: { entity: 'payout', action: 'paid' },
  PAYOUT_CANCELLED: { entity: 'payout', action: 'cancelled' },
  PAYMENT_CANCELLED: { entity: 'payment', action: 'cancelled' },
  COMPANY_POSTPAID_REQUESTED: { entity: 'company', action: 'postpaid_requested' },
  COMPANY_POSTPAID_DECIDED: { entity: 'company', action: 'postpaid_decided' },
  COMPANY_POSTPAID_SUSPENDED: { entity: 'company', action: 'postpaid_suspended' },
  COMPANY_POSTPAID_RESUMED: { entity: 'company', action: 'postpaid_resumed' },
} as const satisfies Record<string, { entity: AuditEntity; action: AuditAction }>

export type AuditEventKey = keyof typeof AUDIT_EVENTS
export type AuditEvent = (typeof AUDIT_EVENTS)[AuditEventKey]

/** 운영자 조회 응답의 한 행. actor는 삭제/시스템일 수 있어 nullable. */
export const auditLogView = z.object({
  id: z.number().int(),
  actorId: z.number().int().nullable(),
  actorName: z.string().nullable(),
  entity: auditEntity,
  action: auditAction,
  entityId: z.number().int().nullable(),
  /** 대상의 사람이 읽을 라벨(기업명·주문번호·업체명 등). 대상이 삭제됐으면 null → 화면은 #id로 폴백. */
  entityLabel: z.string().nullable(),
  payload: z.record(z.unknown()),
  ip: z.string().nullable(),
  createdAt: z.string(),
})
export type AuditLogView = z.infer<typeof auditLogView>

/** cursor 페이지네이션. created_at desc, id를 tie-breaker로. cursor는 마지막 행의 id. */
export const auditLogListResponse = z.object({
  items: z.array(auditLogView),
  nextCursor: z.number().int().nullable(),
})
export type AuditLogListResponse = z.infer<typeof auditLogListResponse>

export const auditLogListQuery = z.object({
  entity: auditEntity.optional(),
  action: auditAction.optional(),
  entityId: z.coerce.number().int().positive().max(9_007_199_254_740_991).optional(),
  actorId: z.coerce.number().int().positive().max(9_007_199_254_740_991).optional(),
  cursor: z.coerce.number().int().positive().max(9_007_199_254_740_991).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
})
export type AuditLogListQuery = z.infer<typeof auditLogListQuery>
